/**
 * All known document categories returned by the order/quotes APIs.
 * Single source of truth — import `DOCUMENT_CATEGORY` for runtime values
 * and `DocumentCategory` for the union type.
 */
export const DOCUMENT_CATEGORY = {
  DELIVERY_ACCEPTANCE: 'DELIVERY_ACCEPTANCE',
  PROFORMA_INVOICE: 'PROFORMA_INVOICE',
  FINAL_QUALITY_CHECK: 'FINAL_QUALITY_CHECK',
  FINAL_QUALITY_CHECK_OTHER: 'FINAL_QUALITY_CHECK_OTHER',
  DUE_BILL: 'DUE_BILL',
  ORDER_AGREEMENT: 'ORDER_AGREEMENT',
  FINAL_INVOICE: 'FINAL_INVOICE',
  DRIVER_LICENSE: 'DRIVER_LICENSE',
  PASSPORT: 'PASSPORT',
  SIGNED_QUOTE_DOCUMENT: 'SIGNED_QUOTE_DOCUMENT',
  FINANCING_AGREEMENT: 'FINANCING_AGREEMENT',
  BANK_QUOTATION: 'BANK_QUOTATION',
  GENERIC: 'GENERIC',
  OTHER: 'OTHER',
} as const;

export type DocumentCategory = (typeof DOCUMENT_CATEGORY)[keyof typeof DOCUMENT_CATEGORY];

/**
 * FileHub module names used to attribute file uploads/downloads to their owning domain
 * (sent as `attributes.entityType` on upload and `module_name` on download-url).
 * Single source of truth — import `FILEHUB_MODULE_NAME` for runtime values
 * and `FilehubModuleName` for the union type.
 */
export const FILEHUB_MODULE_NAME = {
  PROSPECT_MANAGEMENT: 'PROSPECT_MANAGEMENT',
  ORDER_MANAGEMENT: 'ORDER_MANAGEMENT',
  QUOTE_MANAGEMENT: 'QUOTE_MANAGEMENT',
  DELIVERY_MANAGEMENT: 'DELIVERY_MANAGEMENT',
} as const;

export type FilehubModuleName = (typeof FILEHUB_MODULE_NAME)[keyof typeof FILEHUB_MODULE_NAME];

/**
 * Locale-aware personal title options for dropdown selection.
 * Single source of truth used across ProspectDesk and OrderHub.
 *
 * @module titleOptions
 */

type Labels = Record<string, string> | undefined;

type TitleOption = {
  label: string;
  value: string;
};

type TitleOptionEntry = {
  value: string;
  labelKey: string;
  fallbackLabel: string;
  locales: string[];
};

/**
 * Master list of all title options across locales.
 * Each entry specifies which locales it applies to ('*' = all).
 * Default labels are always English — locale-specific labels come from Contentful.
 */
const TITLE_OPTIONS_MASTER: TitleOptionEntry[] = [
  { value: 'Mr.', labelKey: 'const.titleOptions.mr', fallbackLabel: 'Mr.', locales: ['*'] },
  { value: 'Mrs.', labelKey: 'const.titleOptions.mrs', fallbackLabel: 'Mrs.', locales: ['en-us'] },
  { value: 'Ms.', labelKey: 'const.titleOptions.ms', fallbackLabel: 'Ms.', locales: ['en-us'] },
  { value: 'Ms.', labelKey: 'const.titleOptions.miss', fallbackLabel: 'Miss', locales: ['de-de'] },
  { value: 'Ms.', labelKey: 'const.titleOptions.miss', fallbackLabel: 'Miss', locales: ['nl-nl'] },
  {
    value: 'Diverse',
    labelKey: 'const.titleOptions.diverse',
    fallbackLabel: 'Diverse',
    locales: ['de-de', 'nl-nl'],
  },
  { value: 'Dr.', labelKey: 'const.titleOptions.dr', fallbackLabel: 'Dr.', locales: ['*'] },
];

const SUPPORTED_LOCALES = ['en-us', 'de-de', 'nl-nl'];

/**
 * Returns locale-aware personal title options for dropdown selection.
 *
 * @param labels - i18n labels from Contentful
 * @param locale - User locale in lowercase-hyphen format (e.g. 'en-us', 'de-de')
 * @returns Array of title options with label and value properties
 *
 * @example
 * // English (default)
 * getTitleOptions(labels)           // [{ label: 'Mr.', value: 'Mr.' }, ...]
 * getTitleOptions(labels, 'en-us')  // [{ label: 'Mr.', value: 'Mr.' }, ...]
 *
 * // German
 * getTitleOptions(labels, 'de-de')  // [{ label: 'Mr.', value: 'Mr.' }, ...]
 */
export const getTitleOptions = (labels?: Labels, locale?: string): TitleOption[] => {
  const normalizedLocale = locale?.toLowerCase();
  const effectiveLocale =
    normalizedLocale && SUPPORTED_LOCALES.includes(normalizedLocale) ? normalizedLocale : 'en-us';

  return TITLE_OPTIONS_MASTER.filter(
    (opt) => opt.locales.includes('*') || opt.locales.includes(effectiveLocale)
  ).map((opt) => ({
    label: labels?.[opt.labelKey] ?? opt.fallbackLabel,
    value: opt.value,
  }));
};
